/**
 * #4762: running the sign-out teardown so that one step which never finishes cannot hold
 * the loading screen up for ever.
 *
 * `app.component.ts`'s `logonChangedAsync` closes everything belonging to the account being
 * signed out, and only then calls `markAppLoadingAsync(false)` — the one call that takes the
 * loading screen back down. So an unbounded step there does not merely lose us that step, it
 * leaves the user looking at a frozen app. That is not hypothetical; it happened, and
 * design-docs/boot-sequence.md carries the trace and the reasoning.
 *
 * Two things make giving up on a step safe rather than reckless: the login page reloads and
 * wipes local data moments later anyway, so a handle we failed to close is destroyed
 * regardless; and nothing is swallowed, so a hang still reaches a future debugger.
 *
 * It lives in its own file, free of Angular, so the behaviour can be tested directly —
 * `logonChangedAsync` sits on a component with a great many injected services and cannot be
 * driven from a unit test. `signout-teardown.test.ts` also carries source tripwires that fail
 * if the teardown goes back to awaiting bare promises, or if the bounds ever add up to more
 * than the splash watchdog allows.
 */

import { timeout, TimeoutError } from '@cf-shared/shared-util';
import { errorReportFields } from '../services/error-patterns';
import { LocalOpTracker, localOpTracker } from '../services/local-op-tracker';

/** One step of the sign-out teardown. */
export interface TeardownStep {
  /**
   * Short name for this step, used to say WHICH step gave up when one is reported.
   * Keep it stable — it is the grouping key a reader searches Sentry for.
   */
  name: string;
  /**
   * How long to wait for this step before giving up on it, in milliseconds.
   * These are backstops rather than budgets: a healthy teardown finishes every step in
   * well under a second.
   */
  timeoutMs: number;
  /** The work itself. */
  run: () => Promise<unknown>;
  /**
   * Optional: extra fields for the report, read only when this step gives up. A database step
   * uses it to say which wait its shutdown was stuck in (df-5c749cefc673). Call names, enums
   * and numbers only, never document ids or values.
   */
  describeOnGiveUp?: () => { [key: string]: string | number | undefined };
}

/** The part of LoggingService this runner needs, named so the runner stays testable. */
export interface TeardownProblemReporter {
  trackSilentProblem(name: string, context: { [key: string]: any }): void;
}

/** The problem name every gave-up teardown step is reported under. */
export const SIGNOUT_TEARDOWN_GAVE_UP_PROBLEM = 'SignOutTeardownStepGaveUp';

/**
 * Run each step in order, and never let one of them stop the rest.
 *
 * Steps run one after another rather than all at once, because that is what the teardown
 * did before this function existed and the order carries real meaning: the presence
 * heartbeats stop before the databases close, and the databases close before the one this
 * device still needs. Running them together would be a behaviour change nobody asked for.
 *
 * Always resolves. A step that fails is reported, and the next one runs.
 */
export async function runBoundedTeardownStepsAsync(
  steps: TeardownStep[],
  reporter: TeardownProblemReporter,
): Promise<void> {
  for (const step of steps) {
    try {
      await timeout(step.timeoutMs, step.run());
    } catch (err) {
      // Two very different things land here and they need different follow-ups, so say
      // which one happened: a timeout means the step is wedged and something upstream is
      // never settling, while a throw means the step itself failed. This is the same split
      // ReloadTelemetryFlushGaveUp already reports on the sibling reload path.
      reporter.trackSilentProblem(SIGNOUT_TEARDOWN_GAVE_UP_PROBLEM, {
        ...describeGiveUp(step),
        ...errorReportFields(err),
        step: step.name,
        gave_up_reason: err instanceof TimeoutError ? 'timeout' : 'step_threw',
        timeout_ms: step.timeoutMs,
      });
    }
  }
}

/**
 * The step's own extra report fields. A describer that throws must not cost us the report or
 * the steps after it, so its failure is reported as one more field instead.
 */
function describeGiveUp(step: TeardownStep): { [key: string]: string | number | undefined } {
  if (!step.describeOnGiveUp) return {};
  try {
    return step.describeOnGiveUp();
  } catch (err) {
    return { describe_failed: errorReportFields(err).message ?? 'unknown' };
  }
}

/**
 * The give-up fields for a database step: where its shutdown was (`describeShutdown()` on the
 * wrapper or on `UserDb`), plus the oldest local database call still outstanding, which is what
 * a shutdown waiting for its read sections is usually waiting on (df-5c749cefc673).
 *
 * Takes the description as a plain value, not the wrapper, so this file stays free of the
 * database layer as well as of Angular. `undefined` means the service had no database open.
 */
export function describeDatabaseShutdownForReport(
  shutdown: { shutdown_phase: string; shutdown_open_readers?: number } | undefined,
  tracker: LocalOpTracker = localOpTracker,
): { [key: string]: string | number | undefined } {
  return {
    shutdown_phase: shutdown?.shutdown_phase ?? 'no_database',
    shutdown_open_readers: shutdown?.shutdown_open_readers,
    ...tracker.snapshot(),
  };
}
